import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check, Copy, Loader2, ExternalLink, Lock } from 'lucide-react';
import {
  Eyebrow,
  RaceBib,
  RefundWindowIndicator,
  PlusPaymentPlanIndicator,
  ServiceErrorState,
} from '@/components/foundry';
import {
  getProductLibraryForUser,
  type ProductLibrarySnapshot,
} from '@/services/productLibrary';
import {
  getMembershipSnapshot,
  type MembershipSnapshot,
} from '@/services/membership';
import { createBillingPortalSession, createBillingCheckoutSession } from '@/services/api';
import { useAppAuth, useAppUser } from '@/lib/appAuth';
import { LEGAL_LINK_LIST, legalAbsoluteUrl } from '@/lib/legalLinks';
import McpApiKeysSection from '../components/McpApiKeysSection';

// Slice 3 (2026-04-27 acquisition reset): focused billing/account surface.
//
// The Library is the Kajabi-style product browser. Settings is the action surface for
// account + billing — single line subscription state, "Manage in Stripe" portal link,
// owned bumps inline, account email. Deeper personalization (notifications, MCP keys,
// custom domains) is deferred per §9 of the plan.

export default function Settings() {
  const { getToken } = useAppAuth();
  const { user } = useAppUser();
  const [snapshot, setSnapshot] = useState<ProductLibrarySnapshot | null>(null);
  const [membership, setMembership] = useState<MembershipSnapshot | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [actionState, setActionState] = useState<'idle' | 'redirecting' | 'error'>('idle');
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const token = (await getToken?.({ skipCache: true })) ?? null;
        const userId = user?.id;
        const [legacy, membershipResult] = await Promise.all([
          getProductLibraryForUser(token),
          userId ? getMembershipSnapshot(userId, token) : Promise.resolve(null),
        ]);
        if (cancelled) return;
        if (!legacy) {
          setState('error');
          setError("We couldn't load your account.");
          return;
        }
        setSnapshot(legacy);
        setMembership(membershipResult);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "We couldn't load your account.");
        setState('error');
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [getToken, user?.id]);

  async function handleSwitchToCoreAnnual() {
    if (actionState === 'redirecting') return;
    setActionState('redirecting');
    setActionError(null);
    try {
      const session = await createBillingCheckoutSession({
        planId: 'core_annual_hidden',
        cadence: 'annual',
        successUrl: `${window.location.origin}/?surface=os&osRoute=settings&checkout=success&plan=core_annual`,
        cancelUrl: `${window.location.origin}/?surface=os&osRoute=settings&checkout=cancelled`,
      });
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      throw new Error("We couldn't start the checkout");
    } catch (err) {
      setActionState('error');
      setActionError(
        err instanceof Error ? err.message : "We couldn't start the switch to annual",
      );
    }
  }

  async function handleOpenPortal() {
    if (actionState === 'redirecting') return;
    setActionState('redirecting');
    setActionError(null);
    try {
      const portal = await createBillingPortalSession(window.location.href);
      if (portal?.url) {
        window.location.href = portal.url;
        return;
      }
      throw new Error("We couldn't open your billing details");
    } catch (err) {
      setActionState('error');
      setActionError(err instanceof Error ? err.message : "We couldn't open your billing details");
    }
  }

  async function handleSubscribeLab() {
    if (actionState === 'redirecting') return;
    setActionState('redirecting');
    setActionError(null);
    try {
      const session = await createBillingCheckoutSession({
        planId: 'lab_monthly',
        cadence: 'monthly',
        successUrl: `${window.location.origin}/?surface=os&osRoute=settings&checkout=success`,
        cancelUrl: `${window.location.origin}/?surface=os&osRoute=settings&checkout=cancelled`,
      });
      if (session?.url) {
        window.location.href = session.url;
        return;
      }
      throw new Error("We couldn't start the checkout");
    } catch (err) {
      setActionState('error');
      setActionError(err instanceof Error ? err.message : "We couldn't start the checkout");
    }
  }

  if (state === 'loading') {
    return (
      <div className="mx-auto max-w-[960px] px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid place-items-center gap-3 py-24">
          <Loader2 className="h-5 w-5 animate-spin text-fd-text-muted" />
          <p className="font-geist-mono text-[11px] font-semibold uppercase tracking-[0.10em] text-fd-text-subtle">
            Loading your account…
          </p>
        </div>
      </div>
    );
  }

  if (state === 'error' || !snapshot) {
    return (
      <div className="mx-auto max-w-[720px] px-4 py-16 sm:px-6 lg:px-8">
        <ServiceErrorState
          error={error}
          intent="load"
          surface="Your account"
          onRetry={() => window.location.reload()}
          onSignIn={() => { window.location.href = '/sign-in'; }}
        />
      </div>
    );
  }

  const sub = snapshot.subscription;
  const subOneLine = describeSubscription(sub);
  const ownsAnyMain =
    snapshot.products.kickstart.ownership === 'owned' ||
    snapshot.products.brandLabOs.ownership === 'owned';

  return (
    <div className="mx-auto max-w-[960px] px-4 py-7 sm:px-6 lg:px-8">
      <header className="mb-8">
        <Eyebrow className="mb-2">ACCOUNT · BILLING</Eyebrow>
        <h1 className="font-geist text-[30px] font-semibold leading-[1.1] tracking-[-0.025em] text-fd-text sm:text-[36px]">
          Settings
        </h1>
        <p className="mt-3 max-w-[560px] text-[14px] leading-[1.55] text-fd-text-muted">
          Cards, invoices, address, and cancellations are handled in Stripe. Your
          products live on the home page. This is where the account controls are.
        </p>
      </header>

      {/* ACCOUNT card */}
      <section
        className="mb-6 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-6"
        style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
      >
        <Eyebrow className="mb-3 block">ACCOUNT</Eyebrow>
        <dl className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-1">
            <dt className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
              Email
            </dt>
            <dd className="font-geist text-[14px] text-fd-text">
              {user?.primaryEmailAddress?.emailAddress ?? '—'}
            </dd>
          </div>
          <div className="grid gap-1">
            <dt className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
              Support reference
            </dt>
            <dd>
              <SupportReferenceCopy userId={snapshot.userId} />
            </dd>
          </div>
        </dl>
      </section>

      {/* SUBSCRIPTION + PORTAL card */}
      <section
        className="mb-6 rounded-[14px] border-[1.5px] border-fd-text bg-fd-paper p-6"
        style={{ boxShadow: '4px 4px 0 0 var(--fd-text)' }}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <Eyebrow className="mb-1.5 block">THE LAB · SUBSCRIPTION</Eyebrow>
            <p className="font-geist text-[16px] font-semibold leading-[1.3] tracking-[-0.01em] text-fd-text">
              {subOneLine}
            </p>
          </div>
          <RaceBib n="" label={subscriptionBibLabel(sub.state)} tone={subscriptionBibTone(sub.state)} size="sm" shadow="sm" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {(sub.state === 'active' || sub.state === 'cancelling' || sub.state === 'past_due') && (
            <button
              type="button"
              onClick={handleOpenPortal}
              disabled={actionState === 'redirecting'}
              className="inline-flex h-10 items-center gap-2 rounded-[4px] border border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent-deep disabled:cursor-not-allowed disabled:opacity-70"
            >
              {actionState === 'redirecting' ? 'Opening…' : 'Manage in Stripe'}
              {actionState !== 'redirecting' && <ExternalLink className="h-3 w-3" />}
            </button>
          )}
          {(sub.state === 'grace' || sub.state === 'inactive') && (
            <button
              type="button"
              onClick={handleSubscribeLab}
              disabled={actionState === 'redirecting'}
              className="inline-flex h-10 items-center gap-2 rounded-[4px] border border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent-deep disabled:cursor-not-allowed disabled:opacity-70"
            >
              {actionState === 'redirecting' ? 'Opening…' : 'Start Lab subscription'}
              {actionState !== 'redirecting' && <ArrowRight className="h-3 w-3" />}
            </button>
          )}
          {sub.state === 'never_started' && !ownsAnyMain && (
            <a
              href="/?surface=kickstart"
              className="inline-flex h-10 items-center gap-2 rounded-[4px] border border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent-deep"
            >
              Open Kickstart
              <ArrowRight className="h-3 w-3" />
            </a>
          )}
          {sub.state === 'never_started' && ownsAnyMain && snapshot.blueprint && (
            <a
              href={`/bridge/${snapshot.blueprint.blueprintId}`}
              className="inline-flex h-10 items-center gap-2 rounded-[4px] border border-fd-text bg-fd-accent px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-colors hover:bg-fd-accent-deep"
            >
              Open your bridge
              <ArrowRight className="h-3 w-3" />
            </a>
          )}
          {sub.currentPeriodEndAt && (
            <p className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
              {sub.state === 'cancelling' ? 'Ends' : sub.state === 'grace' ? 'Subscription begins' : 'Renews'}{' '}
              {formatDate(sub.currentPeriodEndAt)}
            </p>
          )}
        </div>
        {actionState === 'error' && actionError && (
          <p
            role="alert"
            className="mt-4 rounded-[6px] border border-fd-coral/40 bg-fd-coral-soft px-3 py-2 font-geist text-[12.5px] leading-[1.4] text-fd-text"
          >
            {actionError}.
          </p>
        )}
      </section>

      {/* MEMBERSHIP STATUS — Plus refund window + payment-plan indicator */}
      {membership?.subscriptionState ? (
        <section className="mb-6 grid grid-cols-1 gap-4">
          {membership.subscriptionState.plan === 'plus' ? (
            <RefundWindowIndicator
              refundEligibleUntil={membership.subscriptionState.refund_eligible_until ?? null}
              buildBriefConfirmationAcceptedAt={
                membership.subscriptionState.build_brief_confirmation_accepted_at ?? null
              }
              coreAccessUntil={membership.entitlements.core_access_until ?? null}
              planLabel="Plus"
            />
          ) : null}
          {membership.paymentPlans.length > 0 ? (
            <PlusPaymentPlanIndicator
              plans={membership.paymentPlans.map((p) => ({
                planId: p.plan_id,
                status: p.status,
                installmentsTotal: p.installments_total,
                installmentsPaid: p.installments_paid,
                amountTotal: p.amount_total,
                amountPaid: p.amount_paid,
                currency: p.currency,
                featureFlagEnabled: p.feature_flag_enabled,
              }))}
            />
          ) : null}
        </section>
      ) : null}

      {/* HIDDEN CORE ANNUAL OPT-IN — only for active Core Monthly members.
          Plus members already include 12 months of Core; Core Annual is not surfaced.
          Core Annual is intentionally never on the bridge / pricing pages — quiet
          in-product opt-in only, per membership-led offer architecture. */}
      {membership?.subscriptionState?.plan === 'core_monthly' &&
      membership.subscriptionState.plan_status !== 'canceled' &&
      membership.subscriptionState.plan_status !== 'cancelled' ? (
        <section className="mb-6 rounded-[14px] border border-fd-border bg-fd-paper p-6">
          <Eyebrow className="mb-3 block">SAVE WITH ANNUAL</Eyebrow>
          <p className="font-geist text-[15px] leading-[1.5] text-fd-text">
            Switch your Core membership to annual and save versus monthly.
          </p>
          <p className="mt-2 max-w-[560px] text-[13px] leading-[1.5] text-fd-text-muted">
            We don't advertise this anywhere else. It's here for members who'd rather pay
            once a year and stop thinking about it. What's left of your current month goes
            toward the annual price.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSwitchToCoreAnnual}
              disabled={actionState === 'redirecting'}
              className="inline-flex h-10 items-center gap-2 rounded-[4px] border border-fd-text bg-fd-paper px-4 font-geist-mono text-[12px] font-bold uppercase tracking-[0.04em] text-fd-text transition-transform hover:-translate-y-px active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-70"
            >
              {actionState === 'redirecting' ? 'Opening…' : 'Switch to annual'}
              {actionState !== 'redirecting' && <ArrowRight className="h-3 w-3" />}
            </button>
            <p className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.05em] text-fd-text-subtle">
              Only shown here · not on the pricing page
            </p>
          </div>
        </section>
      ) : null}

      {/* OWNED BUMPS — inline, no separate page */}
      {snapshot.bumps.length > 0 && (
        <section className="mb-6 rounded-[14px] border border-fd-border bg-fd-paper p-6">
          <Eyebrow className="mb-3 block">ADD-ONS · {snapshot.bumps.length}</Eyebrow>
          <ul className="grid gap-2">
            {snapshot.bumps.map((bump) => (
              <li
                key={bump.productId + bump.purchasedAt}
                className="flex flex-wrap items-center justify-between gap-3 rounded-[8px] border border-fd-border bg-fd-bg p-3"
              >
                <span className="font-geist text-[13.5px] font-medium text-fd-text">
                  {bump.name}
                </span>
                <span className="font-geist-mono text-[10.5px] font-semibold uppercase tracking-[0.06em] text-fd-text-subtle">
                  Purchased {formatDate(bump.purchasedAt)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* LEGAL — canonical reference docs; links open the marketing-origin
          /legal/<slug> pages in a new tab so the operator stays in the OS. */}
      <section className="mb-6 rounded-[14px] border border-fd-border bg-fd-paper p-6">
        <Eyebrow className="mb-3 block">LEGAL · {LEGAL_LINK_LIST.length}</Eyebrow>
        <p className="mb-4 max-w-[560px] text-[13px] leading-[1.5] text-fd-text-muted">
          The documents that cover your account, your data, and how we work
          together. Each one opens on theaibrandlab.com.
        </p>
        <ul className="grid gap-2 sm:grid-cols-2">
          {LEGAL_LINK_LIST.map((link) => (
            <li key={link.slug}>
              <a
                href={legalAbsoluteUrl(link.slug)}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex w-full items-center justify-between gap-3 rounded-[8px] border border-fd-border bg-fd-bg px-3 py-2.5 transition-colors hover:border-fd-text/40 hover:bg-fd-surface-1"
              >
                <span className="inline-flex items-baseline gap-2.5">
                  <span className="font-geist-mono text-[10.5px] font-semibold tabular-nums text-fd-text-subtle group-hover:text-fd-text-muted">
                    {link.number}
                  </span>
                  <span className="font-geist text-[13.5px] font-medium text-fd-text">
                    {link.title}
                  </span>
                </span>
                <ExternalLink className="h-3.5 w-3.5 text-fd-text-subtle transition-colors group-hover:text-fd-text-muted" />
              </a>
            </li>
          ))}
        </ul>
      </section>

      {/* MCP API Keys — connect Builder repo to AI Brand Lab for receipt push */}
      <div className="mb-6">
        <McpApiKeysSection />
      </div>

      {/* DEFERRED — placeholder for the bigger settings rebuild */}
      <section className="rounded-[14px] border border-dashed border-fd-border-strong/40 bg-fd-paper p-6">
        <div className="flex items-start gap-3">
          <Lock className="mt-0.5 h-4 w-4 shrink-0 text-fd-text-subtle" />
          <div>
            <Eyebrow className="block">COMING LATER</Eyebrow>
            <p className="mt-1.5 max-w-[560px] text-[13px] leading-[1.5] text-fd-text-muted">
              Notifications, custom domains, and adding other people aren't here yet.
              Payment details, address, and tax are all handled in Stripe.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────

function describeSubscription(sub: ProductLibrarySnapshot['subscription']): string {
  switch (sub.state) {
    case 'active':
      return 'Active. Renews monthly.';
    case 'grace':
      return sub.graceDaysRemaining
        ? `Included with your OS purchase. ${sub.graceDaysRemaining} day${sub.graceDaysRemaining === 1 ? '' : 's'} left.`
        : 'Included with your OS purchase.';
    case 'cancelling':
      return 'Cancelling at next renewal date. Reactivate any time before then.';
    case 'past_due':
      return 'Payment failed. Stripe is retrying — update your card to avoid losing access.';
    case 'inactive':
      return "Your included 30 days have ended. You can start a subscription whenever you want it back.";
    case 'never_started':
      return "You don't have a Lab subscription running.";
  }
}

function subscriptionBibLabel(state: ProductLibrarySnapshot['subscription']['state']): string {
  switch (state) {
    case 'active':
      return 'Active';
    case 'grace':
      return 'Included';
    case 'cancelling':
      return 'Cancelling';
    case 'past_due':
      return 'Past due';
    case 'inactive':
      return 'Inactive';
    case 'never_started':
      return 'Not started';
  }
}

function subscriptionBibTone(state: ProductLibrarySnapshot['subscription']['state']): 'race' | 'ink' | 'teal' {
  if (state === 'active' || state === 'grace') return 'race';
  if (state === 'past_due' || state === 'cancelling') return 'teal';
  return 'ink';
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ── Support reference copy affordance ────────────────────────────────
//
// The buyer doesn't need their internal user ID staring at them on the
// Account card. They need it once: when they email support. This button
// copies the ID to the clipboard with a 1.6s confirmation flash and a
// small "Use this when emailing support" note for context. Per the audit
// finding (P2 settings.md), this replaces the visible <dd> dump.

function SupportReferenceCopy({ userId }: { userId: string }) {
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => () => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(userId);
      setCopied(true);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
      timeoutRef.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard API can fail in some contexts (insecure origin, etc.).
      // Silent fail is acceptable here — the user can still ask support
      // through the Lab and we'll look them up by email.
    }
  };

  return (
    <div className="flex flex-col items-start gap-1.5">
      <button
        type="button"
        onClick={handleCopy}
        aria-live="polite"
        aria-label={copied ? 'Support reference copied to clipboard' : 'Copy support reference to clipboard'}
        className="inline-flex h-8 items-center gap-1.5 rounded-[5px] border border-fd-border bg-fd-paper px-2.5 font-geist-mono text-[11px] font-bold uppercase tracking-[0.05em] text-fd-text-muted transition-colors hover:border-fd-text hover:text-fd-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fd-text"
      >
        {copied ? (
          <>
            <Check className="h-3 w-3 text-fd-teal" strokeWidth={2.5} />
            <span className="text-fd-teal">Copied</span>
          </>
        ) : (
          <>
            <Copy className="h-3 w-3" strokeWidth={2} />
            Copy support reference
          </>
        )}
      </button>
      <p className="font-geist text-[11.5px] leading-[1.4] text-fd-text-subtle">
        Use this when emailing support so we can find your account.
      </p>
    </div>
  );
}
